import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Shield, Users, Star, Check } from 'lucide-react';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { Page, LayoutSingleColumn, ExternalLink, MembershipBanner } from '../../components';
import CitySearchListings from './CitySearchListings';

import css from './CityPage.module.css';

const SITE_URL = 'https://wetrentals.com';

const buildDefaultFaq = (city, region) => [
  {
    question: `How do I book a boat rental in ${city}?`,
    answer: `Browse boats available in ${city} on WetRentals, choose your dates and times, and book online in minutes. You'll get instant confirmation and the host's contact details.`,
  },
  {
    question: `Do I need a boating license to rent in ${city}?`,
    answer:
      'License requirements depend on the type of boat and local rules. Many rentals do not require one. If a license is required, your host will let you know during booking.',
  },
  {
    question: 'Are life jackets and safety gear included?',
    answer:
      'Yes — life jackets and the required safety equipment are included with every WetRentals booking.',
  },
  {
    question: `What is the cancellation policy for ${city} boat rentals?`,
    answer:
      'Most hosts allow cancellation up to 72 hours before the rental for a full refund. Exact terms are shown on each listing.',
  },
  {
    question: `When is the best time to rent a boat in ${city}, ${region}?`,
    answer: `Peak boating season in ${city} runs from late May through early October. Book early on weekends — popular boats fill up fast.`,
  },
  {
    question: 'Can I list my own boat on WetRentals?',
    answer:
      'Yes. Boat owners can earn passive income by listing on WetRentals. We handle bookings and payments — you set the price and availability.',
  },
];

const extractBoundsFromSearchUrl = url => {
  if (!url) return null;
  const match = url.match(/[?&]bounds=([^&]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

const CityPage = props => {
  const {
    city,
    region = 'Ontario',
    country = 'Canada',
    routePath,
    image,
    imageAlt,
    intro,
    sections = [],
    searchUrl,
    searchLabel,
    faq,
    currentPage,
    // When true, renders the Ottawa–Rideau Lakes Membership banner under the hero.
    // Set on Ottawa, Brockville, Kingston (corridor cities).
    showMembershipBanner = false,
  } = props;

  const faqItems = faq && faq.length ? faq : buildDefaultFaq(city, region);
  const title = `Boat Rentals in ${city}, ${region} | WetRentals`;
  const description = `Rent boats, Sea-Doos, jet skis, pontoons and more in ${city}, ${region}. Book trusted local hosts on WetRentals — instant confirmation, included safety gear, flexible cancellation.`;
  const canonicalUrl = `${SITE_URL}${routePath}`;

  const slug = s => s.toLowerCase().replace(/\s+/g, '');
  const countrySlug = slug(country);
  const regionSlug = slug(region);

  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      {
        '@type': 'ListItem',
        position: 2,
        name: country,
        item: `${SITE_URL}/destinations/${countrySlug}/boat-rentals`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: region,
        item: `${SITE_URL}/destinations/${countrySlug}/${regionSlug}/boat-rentals`,
      },
      { '@type': 'ListItem', position: 4, name: city, item: canonicalUrl },
    ],
  };

  const service = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Boat Rentals in ${city}`,
    serviceType: 'Boat rental',
    description,
    provider: { '@type': 'Organization', name: 'WetRentals', url: SITE_URL },
    areaServed: {
      '@type': 'City',
      name: city,
      containedInPlace: {
        '@type': 'AdministrativeArea',
        name: region,
        containedInPlace: { '@type': 'Country', name: country },
      },
    },
    url: canonicalUrl,
  };

  const faqPage = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };

  const schema = [breadcrumbList, service, faqPage];
  const bounds = extractBoundsFromSearchUrl(searchUrl);

  return (
    <Page title={title} description={description} schema={schema} scrollingDisabled={false}>
      <LayoutSingleColumn
        topbar={<TopbarContainer currentPage={currentPage} />}
        footer={<FooterContainer />}
      >
        <div className={css.root}>
          {/* Hero */}
          <section className={css.hero}>
            {image ? (
              <img
                className={css.heroImage}
                src={image}
                alt={imageAlt || `Boat rentals in ${city}, ${region}`}
                loading="eager"
              />
            ) : null}
            <div className={css.heroOverlay} />
            <div className={css.heroContent}>
              <nav className={css.heroBreadcrumb} aria-label="breadcrumb">
                <Link to="/">Home</Link>
                <span> / </span>
                <Link to={`/destinations/${countrySlug}/boat-rentals`}>{country}</Link>
                <span> / </span>
                <Link to={`/destinations/${countrySlug}/${regionSlug}/boat-rentals`}>{region}</Link>
                <span> / </span>
                <span aria-current="page">{city}</span>
              </nav>
              <h1 className={css.heroTitle}>
                Boat Rentals in {city}, {region}
              </h1>
              <p className={css.heroSubtitle}>{intro}</p>
              <div className={css.heroCtaRow}>
                {searchUrl ? (
                  <a href={searchUrl} className={css.heroCta}>
                    {searchLabel || `Browse boats in ${city}`} →
                  </a>
                ) : null}
                <a href="#faq" className={`${css.heroCta} ${css.heroCtaSecondary}`}>
                  Questions? See FAQ
                </a>
              </div>
            </div>
          </section>

          {/* Membership banner (Ottawa–Rideau Lakes corridor only) */}
          {showMembershipBanner ? <MembershipBanner /> : null}

          {/* Feature bar */}
          <div className={css.featureBar}>
            <div className={css.featureBarInner}>
              <div className={css.feature}>
                <span className={css.featureIcon}>
                  <Calendar />
                </span>
                Instant booking
              </div>
              <div className={css.feature}>
                <span className={css.featureIcon}>
                  <Shield />
                </span>
                Safety gear included
              </div>
              <div className={css.feature}>
                <span className={css.featureIcon}>
                  <Users />
                </span>
                Trusted local hosts
              </div>
              <div className={css.feature}>
                <span className={css.featureIcon}>
                  <Star />
                </span>
                Flexible cancellation
              </div>
            </div>
          </div>

          {/* Body + sidebar */}
          <div className={css.bodyWrapper}>
            <div className={css.contentMain}>
              {sections.map((section, idx) => (
                <React.Fragment key={idx}>
                  {section.heading ? <h2>{section.heading}</h2> : null}
                  {section.body ? (
                    <p className={idx === 0 ? css.lead : undefined}>{section.body}</p>
                  ) : null}
                </React.Fragment>
              ))}

              <div className={css.contentSocial}>
                Follow WetRentals:{' '}
                <ExternalLink href="https://facebook.com/wetrentals">Facebook</ExternalLink>{' · '}
                <ExternalLink href="https://twitter.com/thewetrentals">Twitter</ExternalLink>{' · '}
                <ExternalLink href="https://instagram.com/thewetrentals">Instagram</ExternalLink>
              </div>
            </div>

            <aside className={css.sidebar}>
              <div className={css.sidebarCard}>
                <h3>Ready to get on the water?</h3>
                <p>
                  Browse every available boat in {city} — filter by date, type and price.
                </p>
                {searchUrl ? (
                  <a href={searchUrl} className={css.sidebarCta}>
                    See all {city} rentals
                  </a>
                ) : null}
                <div className={css.sidebarHints}>
                  <span>
                    <Check /> Instant confirmation
                  </span>
                  <span>
                    <Check /> Free cancellation 72h ahead
                  </span>
                  <span>
                    <Check /> Safety gear included
                  </span>
                </div>
              </div>
            </aside>
          </div>

          {/* Listings */}
          <section className={css.listingsSection} aria-labelledby="listings-heading">
            <div className={css.listingsInner}>
              <div className={css.sectionHeader}>
                <h2 id="listings-heading">Available boats in {city}</h2>
                <p>Hand-picked from local hosts in {city} and the surrounding area.</p>
              </div>
              <CitySearchListings bounds={bounds} />
            </div>
          </section>

          {/* FAQ */}
          <section className={css.faqSection} id="faq" aria-labelledby="faq-heading">
            <h2 id="faq-heading">Frequently Asked Questions</h2>
            {faqItems.map((item, idx) => (
              <details key={idx} className={css.faqItem}>
                <summary className={css.faqQuestion}>{item.question}</summary>
                <p className={css.faqAnswer}>{item.answer}</p>
              </details>
            ))}
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default CityPage;
